import { parseCookies, setCookie } from "nookies";

const cookies = parseCookies();

const fnpSalesChannelEnumIds = {
  PHONE_SWITCH_ENUM_ID: "ONLN_MBL_PHONESWITCH",
  DEFAULT_ENUM_ID: "ONLN_MBL_DIRECT",
  PARTNER_UTM_SRC: "WS_PTNR",
  MOBILE_ANDROID_FBIX: "MOBILE_ANDROID_FBIX",
  MOBILE_IOS_FBIX: "MOBILE_IOS_FBIX",
  ONLINE_FB: "ONLINE_FB",
  ONLN_MBL_AFLT_NT: "ONLN_MBL_AFLT_NT",
  ONLN_TAB_AFLT_NT: "ONLN_TAB_AFLT_NT",
};

/**
 * Check if the device is a tablet or iPad.
 * @returns {boolean} - Returns true if the device is a tablet or iPad.
 */
export const isTabletOrIpad = () => {
  const ua = typeof window !== "undefined" ? window?.navigator?.userAgent : "";
  return /Tablet|iPad/i.test(ua);
};

/**
 * Get URL parameter value.
 * @param {string} name - The parameter name.
 * @param {string} [defaultURL] - The default URL to use.
 * @returns {string | undefined} - Returns the parameter value if found, otherwise undefined.
 */
export const getUrlParam = (name, defaultURL = typeof window !== "undefined" ? window.location.href : "") => {
  const results = new RegExp(`[\\?&]${name}=([^&#]*)`).exec(defaultURL);
  return results ? results[1] : undefined;
};

/**
 * Detect if the user agent is from a Facebook extension or mobile device.
 * @returns {string} - The sales channel ID based on the user agent.
 */
const detectFbUserAgent = () => {
  const userAgent = window?.navigator.userAgent || window?.navigator.vendor || window?.opera;
  if (/android/i.test(userAgent)) {
    return fnpSalesChannelEnumIds.MOBILE_ANDROID_FBIX;
  } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return fnpSalesChannelEnumIds.MOBILE_IOS_FBIX;
  }
  return fnpSalesChannelEnumIds.ONLINE_FB;
};

/**
 * Sets the sales channel cookie if not already set.
 * @param {string} fnpSalesChannelEnumId - The sales channel ID to set.
 */
const setSalesChannelCookie = (fnpSalesChannelEnumId) => {
  setCookie({}, "fnpSalesChannelEnumId", fnpSalesChannelEnumId, { path: "/" });
};

/**
 * Get sales channel info based on various checks like device type, UTM parameters, and cookies.
 * @param {boolean} isMobile - Flag indicating if the device is mobile.
 * @returns {object} - Sales channel information, including UTM campaign and sales channel ID.
 */
const getFnpSalesChannelInfo = (isMobile) => {
  const { source } = cookies;
  const defaultSalesChannelId = fnpSalesChannelEnumIds.DEFAULT_ENUM_ID;
  let salesChannelId = getUrlParam("fnpSalesChannelEnumId");

  if (isTabletOrIpad()) {
    if (!salesChannelId && !source) {
      salesChannelId = defaultSalesChannelId;
    }
    if (salesChannelId) {
      salesChannelId = salesChannelId.replace("MBL", "TAB");
    }
  }

  if (typeof window !== "undefined" && window?.navigator?.userAgent?.includes("FBExtensions")) {
    salesChannelId = detectFbUserAgent();
  }

  if (getUrlParam("utm_source") === fnpSalesChannelEnumIds.PARTNER_UTM_SRC) {
    if (!salesChannelId) salesChannelId = defaultSalesChannelId;
    salesChannelId = salesChannelId.replace("MBL", "MBLPTNR");
  }

  if (salesChannelId) {
    setSalesChannelCookie(salesChannelId);
  }

  if (salesChannelId && salesChannelId.includes("&")) {
    salesChannelId = getUrlParam("fnpSalesChannelEnumId", cookies.source);
  }

  if (/phonepe-webview/.test(window?.navigator.userAgent || window?.navigator.vendor || window?.opera)) {
    salesChannelId = fnpSalesChannelEnumIds.PHONE_SWITCH_ENUM_ID;
  }

  let utmSource = "";
  let utmCampaign = "";

  if (!salesChannelId) {
    utmSource = source ? getUrlParam("utm_source", source) : cookies.utm_source;
    utmCampaign = source ? getUrlParam("utm_campaign", source) : cookies.utm_campaign;

    if (utmSource?.toLowerCase() === "affiliate") {
      salesChannelId = isTabletOrIpad()
        ? fnpSalesChannelEnumIds.ONLN_TAB_AFLT_NT
        : fnpSalesChannelEnumIds.ONLN_MBL_AFLT_NT;
    }
  }

  const salesInfo = {
    ...(utmCampaign && !utmCampaign.startsWith("WB_") && { utmCampaign }),
    fnpSalesChannelEnumId: salesChannelId || defaultSalesChannelId,
  };

  if (!isMobile) {
    salesInfo.fnpSalesChannelEnumId = salesInfo.fnpSalesChannelEnumId.replace(/MBL/g, "DSKTP");
  }

  return salesInfo;
};

export default getFnpSalesChannelInfo;
